import { z } from 'zod'

import { apiRequest } from '@/api/client'

const eventSchema = z.object({
  id: z.number(),
  title: z.string(),
  description: z.string().nullable().optional(),
  event_type: z.enum(['market', 'town', 'other']),
  event_date: z.string(),
  end_date: z.string().nullable().optional(),
  start_time: z.string().nullable().optional(),
  end_time: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  is_published: z.boolean(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
})

export type CalendarEvent = z.infer<typeof eventSchema>

export type EventPayload = {
  title: string
  description?: string | null
  event_type: 'market' | 'town' | 'other'
  event_date: string
  end_date?: string | null
  start_time?: string | null
  end_time?: string | null
  location?: string | null
  is_published: boolean
}

/** Public published events, optionally filtered by inclusive date range. */
export function fetchPublishedEvents(range?: { from?: string; to?: string }) {
  const params = new URLSearchParams()
  if (range?.from) params.set('from', range.from)
  if (range?.to) params.set('to', range.to)
  const query = params.toString()
  return apiRequest(`/api/v1/events${query ? `?${query}` : ''}`, (data) =>
    z.object({ events: z.array(eventSchema) }).parse(data),
    { authenticated: false },
  )
}

export function fetchAdminEvents() {
  return apiRequest('/api/v1/events/admin', (data) =>
    z.object({ events: z.array(eventSchema) }).parse(data),
  )
}

export function createEvent(payload: EventPayload) {
  return apiRequest('/api/v1/events', (data) => z.object({ event: eventSchema }).parse(data), {
    method: 'POST',
    body: payload,
  })
}

export function updateEvent(id: number, payload: EventPayload) {
  return apiRequest(`/api/v1/events/${id}`, (data) => z.object({ event: eventSchema }).parse(data), {
    method: 'PATCH',
    body: payload,
  })
}

export function deleteEvent(id: number) {
  return apiRequest(`/api/v1/events/${id}`, (data) => z.object({ ok: z.boolean() }).parse(data), {
    method: 'DELETE',
  })
}
